import { MoreVert } from "@mui/icons-material";
import FilterListOffRoundedIcon from "@mui/icons-material/FilterListOffRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import {
  Badge,
  Box,
  Button,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useArrayState,
  type ArrayState,
} from "@shared/frontend/hooks/use-array-state.hook";
import { useCompactScreen } from "@shared/frontend/hooks/use-compact-screen.hook";
import { useMuiMenu } from "@shared/frontend/hooks/use-mui-menu.hook";
import React, { createContext, useContext, useMemo } from "react";
import { useHyperTable } from "../../HyperTable";
import type { HyperTableFilter, OverrideViewOption, ToolbarAction } from "../../types";
import { FilterMenuItem } from "./FilterMenuItem";
import {
  toolbarButtonSx,
  toolbarCompactBadgeStyle,
  toolbarMenuPaperSx,
  toolbarOverflowExtrasSx,
  toolbarWideBadgeStyle,
} from "./toolbar-button-styles";
import { isHyperTableFilterChild } from "./is-filter-child";
import { ToolbarViewOptions } from "./ToolbarViewOptions";

interface HyperTableToolbarProps<Entity> {
  children?: React.ReactNode;
  dataTour?: string;
  disableViewOptions?: boolean;
  overrideViewOptions?: OverrideViewOption<Entity>[];
  onOverrideViewOptionsChange?: (overrideViewOptions: OverrideViewOption<Entity>[]) => void;
  moreActions?: ToolbarAction[];
  /** Wide screens: left cluster before filters. Compact: inside the overflow menu only. */
  toolbarEndExtras?: React.ReactNode;
}

interface ToolbarFiltersContextValue<Entity = any> {
  filters: HyperTableFilter<Entity>[];
  filterActions: ArrayState<HyperTableFilter<Entity>, string>;
}

const ToolbarFiltersContext = createContext<null | ToolbarFiltersContextValue>(
  null,
);

/** RTL toolbar: tabs on the right, actions/filters/view on the left. */
const TOOLBAR_LAYOUT_SX = {
  direction: "rtl",
  width: "100%",
} as const;

export function useToolbarFilters<T>() {
  const ctx = useContext(ToolbarFiltersContext);
  if (!ctx)
    throw new Error(
      "Toolbar filter context called outside of `HyperTable.Toolbar`",
    );
  return ctx as ToolbarFiltersContextValue<T>;
}

export const HyperTableToolbar = <Entity extends any>(
  props: HyperTableToolbarProps<Entity>,
) => {
  const { children, moreActions = [], disableViewOptions = false, overrideViewOptions, onOverrideViewOptionsChange, toolbarEndExtras, dataTour } = props;
  const [filters, filterActions] = useArrayState<HyperTableFilter<Entity>>([]);

  const { filterActions: activeFilterActions } = useHyperTable<Entity>();
  const isCompact = useCompactScreen();

  const [filterButtonRef, filtersMenuProps, filtersMenu] =
    useMuiMenu<HTMLButtonElement>();
  const [moreActionButtonRef, moreActionsMenuProps, moreActionsMenu] =
    useMuiMenu<HTMLButtonElement>();

  const { filterChildren, tabsChildren } = useMemo(() => {
    const allChildren = React.Children.toArray(children);
    return {
      filterChildren: allChildren.filter(isHyperTableFilterChild),
      tabsChildren: allChildren.filter((child) => !isHyperTableFilterChild(child)),
    };
  }, [children]);

  function clearAllFilters() {
    activeFilterActions.set([]);
  }

  const C =
    ToolbarFiltersContext as React.Context<ToolbarFiltersContextValue<Entity> | null>;

  const badgeStyle = isCompact ? toolbarCompactBadgeStyle : toolbarWideBadgeStyle;

  const doesHaveAnyFilters =
    filterChildren.length > 0 || filters.length > 0;

  const hasOverflowMenu =
    isCompact &&
    (moreActions.length > 0 || toolbarEndExtras != null);

  return (
    <C.Provider value={{ filters, filterActions }}>
      {filterChildren}
      <Stack width="100%" minWidth={0} flexShrink={0} sx={TOOLBAR_LAYOUT_SX} data-tour={dataTour}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          paddingBlock="0.5rem"
          flexWrap="nowrap"
          width="100%"
        >
          {/* Right side (RTL start): tabs / list selector */}
          <Stack
            direction="row"
            alignItems="center"
            flexShrink={0}
            minWidth={0}
          >
            {tabsChildren}
          </Stack>

          {/* Left side (RTL end): buttons + filters + view — filters/view never in overflow */}
          <Stack
            direction="row"
            alignItems="center"
            gap={0.75}
            flexShrink={0}
          >
            {!isCompact &&
              moreActions.map(({ label, onClick, icon }, index) => (
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={toolbarButtonSx}
                  onClick={(e) => onClick(e, "pc")}
                  key={index + "_" + label}
                >
                  {icon}
                  {label}
                </Button>
              ))}
            {!isCompact && toolbarEndExtras}

            {hasOverflowMenu && (
              <>
                <Button
                  ref={moreActionButtonRef}
                  variant="outlined"
                  color="secondary"
                  onClick={moreActionsMenu.toggle}
                  sx={toolbarButtonSx}
                  aria-label="פעולות נוספות"
                >
                  <MoreVert />
                  עוד
                </Button>
                <Menu
                  disablePortal
                  {...moreActionsMenuProps}
                  slotProps={{ paper: { sx: toolbarMenuPaperSx } }}
                >
                  <MenuList dense={false}>
                    <ListItem sx={{ pt: 0.75, pb: 0.5 }}>
                      <Typography color="primary" fontWeight={600} fontSize="0.9rem">
                        פעולות נוספות
                      </Typography>
                    </ListItem>
                    <Divider />
                    {toolbarEndExtras && (
                      <Box
                        sx={toolbarOverflowExtrasSx}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {toolbarEndExtras}
                      </Box>
                    )}
                    {toolbarEndExtras && moreActions.length > 0 && <Divider />}
                    {moreActions.filter(Boolean).map((action, index) => {
                      const { label, onClick, icon } = action!;
                      return (
                        <MenuItem
                          key={index + "_" + label}
                          sx={{ minHeight: "2.5rem", gap: 0.5 }}
                          onClick={(e) => {
                            onClick(e, "mobile");
                            moreActionsMenu.hide();
                          }}
                        >
                          {icon && (
                            <ListItemIcon sx={{ minWidth: "2rem", color: "secondary.main" }}>
                              {icon}
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={label}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              fontWeight: 500,
                            }}
                          />
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              </>
            )}

            {doesHaveAnyFilters && (
              <>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ horizontal: "left", vertical: "top" }}
                  slotProps={{
                    badge: {
                      style: {
                        color: "white",
                        ...badgeStyle,
                      },
                    },
                  }}
                  color="secondary"
                  badgeContent={
                    filters.filter((v) => activeFilterActions.has(v.id)).length
                  }
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={toolbarButtonSx}
                    ref={filterButtonRef}
                    onClick={filtersMenu.show}
                  >
                    <FilterListRoundedIcon aria-hidden="true" />
                    מסננים
                  </Button>
                </Badge>
                <Menu
                  disablePortal
                  {...filtersMenuProps}
                  slotProps={{ paper: { sx: toolbarMenuPaperSx } }}
                >
                  <MenuList sx={{ minWidth: "12rem" }}>
                    <ListItem
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 0.75,
                        pb: 0.5,
                      }}
                    >
                      <Typography color="primary" fontWeight={600} fontSize="0.9rem">
                        סינון לפי
                      </Typography>
                      <Tooltip title="ניקוי מסננים">
                        <Button
                          onClick={clearAllFilters}
                          color="primary"
                          size="small"
                          sx={{ minWidth: "unset", p: 0.5 }}
                        >
                          <FilterListOffRoundedIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </ListItem>
                    <Divider />
                    {filters.map((filter) => (
                      <FilterMenuItem<Entity> key={filter.id} filter={filter} />
                    ))}
                  </MenuList>
                </Menu>
              </>
            )}

            {!disableViewOptions && (
              <ToolbarViewOptions
                overrideViewOptions={overrideViewOptions}
                onOverrideViewOptionsChange={onOverrideViewOptionsChange}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </C.Provider>
  );
};
